import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Cloudinary } from 'services/cloudinary';
import { Util } from 'services/util';
import { XmStore, XmStoreUtil } from 'services/store';
import { BehaviorSubject, Subscription } from 'rxjs';
import { StoreAction } from 'store/actions';
import { CmsAddonPlans } from 'store/cms/models';
import { AddonAction, AddonType, PERCENTAGE_UPDATE_INTERVAL, ProgressStatus, REQUEST_INTERVAL, REQUEST_TIMEOUT, RETRY_REQUEST_COUNT } from 'my-account/constants';
import { AddRemoveAddons } from 'store/global-travel-pass/models/add-remove-addons';
import { AddonRequestStatus } from 'store/global-travel-pass/models';
import { RequestResult } from 'store/line/models';
import { CimaCore } from 'core/services';

@Component({
    selector: 'xm-add-additional-data',
    templateUrl: 'add-additional-data.html',
    styleUrls: ['add-additional-data.scss'],
    standalone: false
})
export class XmAddAdditionalDataModal implements OnInit, OnDestroy {
    public dialogRef: MatDialogRef<XmAddAdditionalDataModal>;
    public pageData: CmsAddAdditionalData;
    public submitPromise: Promise<void | {}>;
    public planData: CmsAddonPlan;
    public addAdditionalData: CmsAddAdditionalData;
    public imagePrimary: MediaImageOptions;
    public failureIcon: MediaImageOptions;
    public features: CmsPendingRemovalFeature[] = [];
    public isApiError: boolean = false;
    public isSuccess: boolean = false;
    public isPending: boolean = false;
    public addonsPollingPercentage$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    public loaderData: CmsLoaderContent;
    public currentStatus: string;
    public addonsPollingStatus$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
    public ProgressStatus: typeof ProgressStatus = ProgressStatus;
    public emailIcon: MediaImageOptions;
    public isBusinessUser: boolean;
    private cimaCore: CimaCore;
    private modalData: CancelRemovalModalData;
    private xmStore: XmStore;
    private subscriptions: Subscription[] = [];
    private state: StateService;
    private pollingRequestId: string;
    private pollingCount: number = 0;
    private addonsSwitchTimeout: NodeJS.Timer;
    private currentPercentage: number = 0;
    private targetPercentage: number = 0;
    private lastAddonsOverallStatus: string;
    private percentageUpdateInterval: NodeJS.Timer;
    private addonsRequestApiInterval: NodeJS.Timer;

    constructor(cimaCore: CimaCore, dialogRef: MatDialogRef<XmAddAdditionalDataModal>, state: StateService, xmStore: XmStore, @Inject(MAT_DIALOG_DATA) modalData: CancelRemovalModalData) {
        Object.assign(this, { cimaCore, xmStore, dialogRef, modalData, state });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.addonsPollingStatus$.next(ProgressStatus.SUMMARY);
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsAddonPlans>(CmsAddonPlans), (result: CmsAddonPlans) => {
            const addonType: string = this.modalData.isGtp ? AddonType.GTP : AddonType.CAN_MEX_UNL_PASS;
            this.planData = result.plans.find((plan: CmsAddonPlan) => plan.gtp1B && plan.type === addonType);
            this.addAdditionalData = this.planData.content.addAdditionalData;
            this.addAdditionalData.header = this.isBusinessUser ? this.planData.content.addAdditionalData.cbmHeader : this.addAdditionalData.header;
            this.loaderData = this.planData.content.loaderData.add.inProgress;
            this.addAdditionalData.features.forEach((feature: CmsPendingRemovalFeatureData) => {
                this.features.push({
                    description: feature.description,
                    imageOption: {
                        mobile: Cloudinary.generateMediaFromCms(feature.icon)
                    }
                });
            });
            this.imagePrimary = {
                mobile: Cloudinary.generateMediaFromCms(this.addAdditionalData.iconPrimary)
            };
            this.failureIcon = {
                mobile: Cloudinary.generateMediaFromCms(this.addAdditionalData.failureIcon)
            };
        }));

        this.subscriptions.push(
            this.addonsPollingStatus$.subscribe((status: string) => {
                if (status) {
                    this.currentStatus = status;
                    if (this.currentStatus === ProgressStatus.SUCCESS) {
                        this.loaderData = this.planData.content.loaderData.add.success;
                    } else if (this.currentStatus === ProgressStatus.PENDING) {
                        this.loaderData = this.planData.content.loaderData.add.pending;
                        this.emailIcon = {
                            mobile: Cloudinary.generateMediaFromCms(this.loaderData?.disclaimerIcon)
                        };
                    } else {
                        this.loaderData = this.planData.content.loaderData.add.inProgress;
                    }
                }
            })
        );
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public close(): void {
        this.dialogRef.close();
        if (this.isSuccess) {
            this.navigateAndClear();
        }
    }

    public onDoneClick(): void {
        this.dialogRef.close();
        this.navigateAndClear();
    }

    public submit(): void {
        this.submitPromise = this.xmStore.persist(StoreAction.POST_ADD_REMOVE_ADDONS, {
            payload: {
                action: 'ADD',
                lineIdKeys: [
                    this.modalData.lineId
                ],
                addOnType: this.modalData.isGtp ? AddonAction.GTP_BOOST : AddonAction.MEXCAN_BOOST
            }
        }).then((addRemovedResponse: AddRemoveAddons) => {
            if (addRemovedResponse.requestId) {
                this.pollingRequestId = addRemovedResponse.requestId;
                this.initAddonsPolling();
            } else {
                this.isApiError = true;
            }
        }).catch(() => this.isApiError = true);
    }

    private navigateAndClear(): void {
        this.state.go('account.activity.landing', {}, { reload: true });
        const clearApisList: StoreDeleteAction[] = [
            { action: StoreAction.CLEAR_GLOBAL_TRAVEL_PASS }
        ];
        this.xmStore.clearApis(clearApisList);
    }

    private initAddonsPolling(): void {
        this.addonsPollingStatus$.next(ProgressStatus.IN_PROGRESS);

        this.pollingCount = 0;
        this.lastAddonsOverallStatus = ProgressStatus.IN_PROGRESS;

        // Reset percentage tracking
        this.currentPercentage = 0;
        this.targetPercentage = 0;
        this.addonsPollingPercentage$.next(0);

        // Start the smooth percentage update interval
        this.startPercentageUpdates();

        // Call the API immediately for the first time
        this.performAddonsPollingCall();

        // Set up interval for subsequent calls
        this.addonsRequestApiInterval = setInterval(() => {
            this.performAddonsPollingCall();
        }, REQUEST_INTERVAL);
    }

    private startPercentageUpdates(): void {
        this.percentageUpdateInterval = setInterval(() => {
            if (this.currentPercentage < this.targetPercentage) {

                const incrementPerUpdate: number = 20 / (REQUEST_INTERVAL / PERCENTAGE_UPDATE_INTERVAL);

                this.currentPercentage = Math.min(this.currentPercentage + incrementPerUpdate, this.targetPercentage);
                this.addonsPollingPercentage$.next(Math.floor(this.currentPercentage));
            }
        }, PERCENTAGE_UPDATE_INTERVAL);
    }

    private performAddonsPollingCall(): void {
        this.pollingCount++;

        this.targetPercentage = Math.min((this.pollingCount / RETRY_REQUEST_COUNT) * 100, 95);

        // Stop polling after 5 attempts
        if (this.pollingCount > RETRY_REQUEST_COUNT) {
            this.completeAddonsPolling(this.lastAddonsOverallStatus);

            return;
        }

        this.queryAddonsRequestStatus().then((addonRequestStatus: AddonRequestStatus) => {
            this.lastAddonsOverallStatus = addonRequestStatus.overallStatus;

            if (addonRequestStatus.overallStatus === ProgressStatus.SUCCESS) {
                this.isSuccess = true;
                this.completeAddonsPolling(ProgressStatus.SUCCESS);
            } else if (addonRequestStatus.overallStatus === ProgressStatus.FAIL) {
                this.isApiError = true;
                this.completeAddonsPolling(addonRequestStatus.overallStatus);
            } else if (this.pollingCount === RETRY_REQUEST_COUNT) {
                // After 5 attempts, move to pending status
                this.isPending = true;
                this.completeAddonsPolling(ProgressStatus.PENDING);
            } else if (!this.addonsSwitchTimeout && addonRequestStatus.overallStatus === ProgressStatus.IN_PROGRESS) {
                // Set timeout only once when got status IN_PROGRESS
                this.addonsSwitchTimeout = setTimeout(() => {
                    this.completeAddonsPolling(this.lastAddonsOverallStatus);
                }, REQUEST_TIMEOUT);
            }
        }).catch(() => {
            // On API error, still respect the polling count limit
            // If not at limit yet, continue polling (the interval will call again)
            this.lastAddonsOverallStatus = ProgressStatus.FAIL;
            this.isApiError = true;

            if (this.pollingCount >= RETRY_REQUEST_COUNT) {
                // If we've hit max retries, clean up and navigate to failure page
                this.completeAddonsPolling(ProgressStatus.FAIL);
                this.isApiError = true;
            }
        });
    }

    private queryAddonsRequestStatus(): Promise<AddonRequestStatus> {
        return this.xmStore.queryPromise<AddonRequestStatus>(StoreAction.GET_ADDONS_REQUEST_STATUS, {
            requestId: this.pollingRequestId
        });
    }

    private completeAddonsPolling(overallStatus?: string): void {
        // Emit the final status
        if (overallStatus) {
            this.addonsPollingStatus$.next(overallStatus);
        }
        // Persist the final status
        XmStoreUtil.defaultCatch(this.xmStore.persist<RequestResult>(StoreAction.SAVE_REQUEST_RESULT, { requestStatus: overallStatus }));
        clearInterval(this.addonsRequestApiInterval);
        clearTimeout(this.addonsSwitchTimeout);
        clearInterval(this.percentageUpdateInterval);
        this.addonsRequestApiInterval = undefined;
        this.addonsSwitchTimeout = undefined;
        this.percentageUpdateInterval = undefined;
        this.pollingCount = 0;

        // Set to 100% when complete
        this.currentPercentage = 100;
        this.targetPercentage = 100;
        this.addonsPollingPercentage$.next(100);
    }
}
